import type { SurplusComponentTrace } from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import {
  componentDescription,
  componentLabel,
  traceReason,
  traceStateLabel,
} from './compositionText';
import {
  surplusQuantity,
  traceHasProvenance,
  traceHasStateValue,
  traceIsDominated,
} from './surplusTracePresentation';

type TraceProps = { row: SurplusComponentTrace };

function TraceValue({ row }: TraceProps) {
  const gt = useGT();
  if (traceHasStateValue(row))
    return (
      <span className="text-muted-foreground">
        {traceStateLabel(row.state, gt)}
      </span>
    );
  return <span>{surplusQuantity(row.quantity)}</span>;
}

function TraceDominance({
  row,
  maximumKnown,
}: TraceProps & { maximumKnown: boolean }) {
  if (row.dominant && maximumKnown)
    return (
      <p>
        <T context="selected component sets the maximum">Sets the maximum.</T>
      </p>
    );
  if (traceIsDominated(row, maximumKnown))
    return (
      <p>
        <T context="selected component not maximum">
          Selected; does not determine the maximum.
        </T>
      </p>
    );
  return null;
}

function TraceStateNote({ row }: TraceProps) {
  const gt = useGT();
  if (!['estimated', 'configured_zero'].includes(row.state)) return null;
  return <p>{traceStateLabel(row.state, gt)}</p>;
}

function TraceSignificance({
  row,
  maximumKnown,
}: TraceProps & { maximumKnown: boolean }) {
  return (
    <>
      <TraceStateNote row={row} />
      <TraceDominance row={row} maximumKnown={maximumKnown} />
    </>
  );
}

function TraceReason({ row }: TraceProps) {
  const gt = useGT();
  return row.reason ? <p>{traceReason(row.reason, gt)}</p> : null;
}

function TraceSalesParameters({ row }: TraceProps) {
  const gt = useGT();
  if (row.kind !== 'historical_customer_sales') return null;
  return (
    <p>
      {gt(
        'Observation: {lookback} complete months · Protection target: {target} months',
        {
          context: 'surplus trace sales window and retention target',
          lookback: row.parameters.lookbackCompleteMonths ?? 12,
          target: row.parameters.coverageMonths ?? '—',
        },
      )}
    </p>
  );
}

function TraceSourceTime({ row }: TraceProps) {
  const gt = useGT();
  return row.sourceAsOf ? (
    <p>
      {gt('Source as of: {date}', {
        context: 'surplus trace source timestamp',
        date: row.sourceAsOf,
      })}
    </p>
  ) : null;
}

function TraceUnit({ row }: TraceProps) {
  const gt = useGT();
  return row.unit ? (
    <p>
      {gt('Comparison unit: {unit}', {
        context: 'surplus trace comparison unit',
        unit: row.unit,
      })}
    </p>
  ) : null;
}

function TraceOrigin({ row }: TraceProps) {
  const gt = useGT();
  if (!row.source) return null;
  const source =
    row.source === 'minimum from this same settings revision'
      ? gt('Minimum from this same settings revision', {
          context: 'surplus composed minimum source provenance',
        })
      : row.source;
  return (
    <p className="break-words">
      {gt('Source: {source}', {
        context: 'surplus trace source provenance',
        source,
      })}
    </p>
  );
}

function TraceProvider({ row }: TraceProps) {
  const gt = useGT();
  return row.providerId ? (
    <p className="break-words font-mono">
      {gt('Provider: {provider} · version {version}', {
        context: 'surplus trace provider provenance',
        provider: row.providerId,
        version: row.providerVersion,
      })}
    </p>
  ) : null;
}

function TraceSourceDetails({ row }: TraceProps) {
  if (!traceHasProvenance(row)) return null;
  return (
    <details>
      <summary className="w-fit cursor-pointer rounded-sm py-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
        <T context="surplus trace source provenance disclosure">
          Source details
        </T>
      </summary>
      <div className="space-y-0.5 pt-0.5">
        <TraceSourceTime row={row} />
        <TraceUnit row={row} />
        <TraceOrigin row={row} />
        <TraceProvider row={row} />
      </div>
    </details>
  );
}

function SelectedTraceDetails({
  row,
  maximumKnown,
}: TraceProps & { maximumKnown: boolean }) {
  return (
    <>
      <TraceSignificance row={row} maximumKnown={maximumKnown} />
      <TraceReason row={row} />
      <TraceSalesParameters row={row} />
      <TraceSourceDetails row={row} />
    </>
  );
}

function OmittedTraceDetails({ row }: TraceProps) {
  const gt = useGT();
  return row.reason ? <p>{componentDescription(row.kind, gt)}</p> : null;
}

/**
 * One component under its operator line: label, amount or state, then the
 * reason it matters, with provider provenance folded under Source details.
 */
export function SurplusTraceRow({
  row,
  maximumKnown = false,
}: TraceProps & { maximumKnown?: boolean }) {
  const gt = useGT();
  return (
    <div className="grid grid-cols-[1rem_minmax(0,1fr)_auto] gap-x-2 gap-y-0.5 py-1 text-xs">
      <span aria-hidden="true" />
      <span className="pl-3">{componentLabel(row.kind, gt)}</span>
      <span className="text-right tabular-nums">
        <TraceValue row={row} />
      </span>
      <div className="col-span-2 col-start-2 space-y-0.5 pl-3 text-muted-foreground">
        {row.state === 'not_selected' ? (
          <OmittedTraceDetails row={row} />
        ) : (
          <SelectedTraceDetails row={row} maximumKnown={maximumKnown} />
        )}
      </div>
    </div>
  );
}
